import { useEffect, useMemo, useState } from "react";
import Header from "../Header";
import Footer from "../Footer";
import { useLanguage, getLocalizedPath } from "../contexts/LanguageContext";
import { trackEvent } from "../utils/analytics";
import s from "./OceanSearchLandingPage.module.scss";

const pages = {
  snorkeling: {
    title: "宮古島のシュノーケルガイド｜おすすめビーチ・海況・注意点",
    navLabel: "シュノーケル",
    conditionLabel: "今日のシュノーケル予報",
    heading: "宮古島でシュノーケルを楽しむなら",
    lead: "おすすめのビーチ、海況の見方、海に入る前の注意点をひとつにまとめました。",
    body: "新城海岸・吉野海岸・インギャーマリンガーデンなど、宮古島にはシュノーケルを楽しめる場所が多くあります。ただし風向き、波、うねりで状況は毎日変わります。出発前にOcean Guideのマップで、今日の場所別評価を確認してください。",
    faq: [
      ["宮古島でシュノーケルにおすすめのビーチは？", "新城海岸、吉野海岸、インギャーマリンガーデンなどが代表的です。その日おすすめの場所は、風と波によって変わります。"],
      ["シュノーケル前に何を確認すればいいですか？", "風向き、風速、波、うねりに加え、現地の白波や流れを確認してください。高波・強風の日は海に入らないでください。"],
    ],
  },
  beach: {
    title: "宮古島のビーチ遊びガイド｜今日おすすめの海・風向き・過ごし方",
    navLabel: "ビーチ遊び",
    conditionLabel: "今日のビーチ遊び予報",
    heading: "宮古島で、今日のビーチを選ぶなら",
    lead: "風向きや潮位に合わせて、ゆっくり過ごしやすいビーチを探せます。",
    body: "与那覇前浜、砂山ビーチ、渡口の浜、17ENDなど、宮古島のビーチは方角や地形で受ける風が異なります。風が強い日でも比較的過ごしやすい場所はありますが、強風や高波の日は海岸へ近づかないでください。",
    faq: [
      ["風が強い日はどのビーチがいいですか？", "風を直接受けにくい方角のビーチを選びます。Ocean Guideのビーチ遊びマップで、場所ごとの評価と風の影響を確認してください。"],
      ["17ENDへ行くおすすめの時間は？", "干潮の前後は砂州が見えやすい時間です。ただし、海況が悪い日は潮位に関係なく海岸へ近づかないでください。"],
    ],
  },
  stargazing: {
    title: "宮古島の星空ガイド｜おすすめ時間・月明かり・星空スポット",
    navLabel: "星空ガイド",
    conditionLabel: "今夜の星空予報",
    heading: "宮古島で星空を見るなら",
    lead: "雲量・月明かり・風を確認し、今夜の星空を楽しみやすい時間を案内します。",
    body: "宮古島は街明かりを離れると星空を楽しめる場所が多くあります。20時以降を目安に、月明かりと雲の量、その日の風を確認して安全な場所から観察してください。",
    faq: [
      ["宮古島で星空を見るおすすめの時間は？", "空が十分暗くなる20時以降が目安です。月明かりや雲の量によって、その日のおすすめ時間は変わります。"],
      ["新月ならいつでも星が見えますか？", "月明かりは少なくなりますが、雲・雨・風の影響は別です。今夜の星空予報で確認してください。"],
    ],
  },
  sunset: {
    title: "宮古島のサンセットガイド｜日の入り時間・おすすめスポット",
    navLabel: "サンセットガイド",
    conditionLabel: "今日のサンセット予報",
    heading: "宮古島でサンセットを見るなら",
    lead: "日の入り時間と空模様を確認し、夕日を楽しみやすい場所と時間を探せます。",
    body: "与那覇前浜、長間浜、17ENDなど、宮古島には西向きの空を楽しめる場所があります。日の入り30〜45分前に到着すると、明るい海から空の色が変わる時間まで楽しめます。",
    faq: [
      ["宮古島のサンセットはどこがおすすめですか？", "与那覇前浜、長間浜、17ENDなどが代表的です。雲の量や風、滞在エリアに合わせて選んでください。"],
      ["曇りの日でもサンセットは見えますか？", "西の空に雲の切れ間があれば、日没前後に色づくことがあります。直前の空模様も確認しましょう。"],
    ],
  },
  "snorkeling-today": {
    title: "今日の宮古島シュノーケル情報",
    navLabel: "今日のシュノーケル",
    lead: "今日の天気・降水確率・風・波を確認して、シュノーケルへ行く前の判断に役立てられます。",
    heading: "今日、宮古島でシュノーケルできる？",
    body: "同じ宮古島でも、風向きによって穏やかな海岸と影響を受けやすい海岸が変わります。Ocean Guideでは、場所ごとの風と波の影響、透明度、おすすめスポットを確認できます。",
    faq: [
      ["今日おすすめの場所はどこですか？", "風向きと波によって毎日変わります。最新のおすすめはOcean Guideのシュノーケル欄で確認できます。"],
      ["雨予報でもシュノーケルできますか？", "雨だけで中止とは限りませんが、雷・強風・高波がある場合は海に入らないでください。"],
    ],
  },
  "windy-day-beaches": {
    title: "風が強い日におすすめの宮古島ビーチ",
    navLabel: "風の日ビーチ",
    lead: "宮古島は風向きによって、風を受けるビーチと比較的穏やかなビーチが変わります。",
    heading: "宮古島で風が強い日はどのビーチを選ぶ？",
    body: "風速だけでなく、風が海から陸へ吹くオンショアか、陸から海へ吹くオフショアかを確認することが重要です。Ocean Guideのマップでは、場所ごとの風と波の影響を表示します。",
    faq: [
      ["風速何mから注意が必要ですか？", "風を直接受ける場所では5m/sを超えると注意、7m/sを超えるとおすすめしない判定になります。"],
      ["風裏なら安全ですか？", "比較的穏やかになりやすいですが、波・うねり・離岸流も確認し、現地が荒れていれば入らないでください。"],
    ],
  },
  "rainy-day": {
    title: "宮古島の雨の日の過ごし方",
    navLabel: "雨の日",
    lead: "雨の日でも楽しめる場所と、雨の合間に動くための最新情報をまとめています。",
    heading: "宮古島が雨の日はどう過ごす？",
    body: "宮古島の雨は場所や時間によって変わることがあります。雨雲の動きを確認しながら、カフェ、ランチ、屋内体験などへ予定を切り替えられます。",
    faq: [
      ["雨の日でも海はきれいですか？", "短時間の雨なら透明度が大きく変わらない場合もありますが、強い雨の後は濁ることがあります。"],
      ["雨の日におすすめの過ごし方は？", "カフェ、ランチ、ものづくり体験などを候補にして、雨雲の切れ間に景色を楽しむのがおすすめです。"],
    ],
  },
  "tomorrow-weather": {
    title: "宮古島の明日の天気・海況",
    navLabel: "明日の天気",
    conditionLabel: "明日の宮古島",
    dateOffset: 1,
    heading: "明日の宮古島、海で遊べる？",
    lead: "明日の天気、降水確率、風、波を確認して、海遊びや観光の予定を立てる前に判断できます。",
    body: "宮古島は風向きやうねりによって、翌日の海況が大きく変わります。Ocean Guideでは、明日のシュノーケル・ビーチ遊び・撮影の目安と、場所ごとの影響を確認できます。",
    faq: [
      ["明日の海況はいつ確認するのが良いですか？", "前日にも目安を確認できますが、風や波の予報は変わるため、当日の朝にOcean Guideで再確認してください。"],
      ["明日が雨予報でも観光できますか？", "短時間の雨であれば予定を調整できることもあります。強風・高波・雷の予報がある場合は、海遊びを控えてください。"],
    ],
  },
  "sunset-today": {
    title: "宮古島の今日のサンセット｜日の入り時間とおすすめスポット",
    navLabel: "今日のサンセット",
    conditionLabel: "今日のサンセット予報",
    heading: "今日、宮古島でサンセットを見るなら？",
    lead: "日の入り時刻、空模様、風を確認して、今日の夕日を楽しみやすい場所を探せます。",
    body: "宮古島のサンセットは、雲の量や風向き、視界で見え方が変わります。Ocean Guideでは、日の入り前後のおすすめ時間と、17END・与那覇前浜・長間浜などのスポットを案内します。",
    faq: [
      ["サンセットは何分前に行けばいいですか？", "日の入り30〜45分前には到着すると、明るい海から空の色が変わる流れまで楽しめます。"],
      ["曇りの日でも夕日は見られますか？", "西の空に雲の切れ間があれば、日没直前や直後に空が色づくことがあります。最新の空模様を確認してください。"],
    ],
  },
  "stargazing-tonight": {
    title: "宮古島の今日の星空｜雲量・月明かり・おすすめ時間",
    navLabel: "今夜の星空",
    conditionLabel: "今夜の星空予報",
    heading: "今夜、宮古島で星空は見える？",
    lead: "雲量、月明かり、風をまとめて確認し、今夜の星空観察に向く時間を探せます。",
    body: "宮古島では月明かりと雲の量によって星の見え方が変わります。Ocean Guideでは、夜の予報から観察しやすい時間を案内します。強風や雨の日は無理に屋外へ出ず、安全を優先してください。",
    faq: [
      ["星空を見るなら何時がおすすめですか？", "日没後に空が十分暗くなる20時以降が目安です。月明かりや雲の量によって、その日のおすすめ時間は変わります。"],
      ["雲量はどのくらいなら星が見えますか？", "雲が多少あっても切れ間から見えることがあります。雲量が70％を超える場合や雨予報では、見えにくくなります。"],
    ],
  },
  "after-typhoon-sea": {
    title: "宮古島の台風後の海｜海況・うねり・海遊びの注意点",
    navLabel: "台風後の海",
    conditionLabel: "今日の台風・海況予報",
    heading: "台風後、宮古島の海はいつ入れる？",
    lead: "台風が過ぎた後も残るうねり、風、波を確認し、海へ行く前に知っておきたい注意点をまとめます。",
    body: "晴れて見えても、台風後はうねりや流れが残ることがあります。海に入れるかは台風が過ぎた日数だけでは判断できません。Ocean Guideで風・波・場所ごとの判定を確認し、ツアーは各事業者の催行状況を確認してください。",
    faq: [
      ["台風の翌日は海に入れますか？", "晴れていても高波や強いうねりが残ることがあります。海岸に白波が立っている、流れが強いなどの場合は海に入らないでください。"],
      ["台風後は海がきれいですか？", "風と波が落ち着いた後は透明度が上がることもあります。ただし、海況が安全なことを確認できてから楽しむことが前提です。"],
    ],
  },
};

const weatherLabel = (code) => {
  if (code <= 0) return "晴れ";
  if (code <= 2) return "晴れ時々曇";
  if (code <= 48) return "曇";
  return "雨の可能性あり";
};

const getDateKey = (offset = 0) => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit",
}).format(new Date(Date.now() + offset * 86_400_000));

const OceanSearchLandingPage = ({ pageKey }) => {
  const { lang } = useLanguage();
  const content = pages[pageKey];
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ""}/ocean-data-cache.json`, { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then(setData)
      .catch(() => setData(null));
  }, []);

  const today = useMemo(() => {
    const key = getDateKey(content.dateOffset || 0);
    const weatherIndex = data?.weather?.daily?.time?.indexOf(key) ?? -1;
    const marineIndex = data?.marine?.daily?.time?.indexOf(key) ?? -1;
    const windItems = (data?.wind?.hourly?.time || []).map((time, index) => ({
      time,
      speed: Number(data.wind.hourly.wind_speed_10m?.[index]),
    })).filter((item) => item.time.startsWith(key) && /T(06|09|12|15|18):/.test(item.time));
    return {
      weather: weatherIndex >= 0 ? weatherLabel(Number(data.weather.daily.weather_code?.[weatherIndex])) : "更新中",
      rain: weatherIndex >= 0 ? data.weather.daily.precipitation_probability_max?.[weatherIndex] : "--",
      wind: windItems.length ? Math.max(...windItems.map((item) => item.speed)).toFixed(1) : "--",
      wave: marineIndex >= 0 ? Number(data.marine.daily.wave_height_max?.[marineIndex]).toFixed(1) : "--",
    };
  }, [content.dateOffset, data]);

  const oceanPath = getLocalizedPath("/ocean-guide", lang);
  const landingLinks = Object.entries(pages).map(([key, page]) => ({ key, label: page.navLabel, href: `/ocean-guide/${key}` }));
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faq.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Header logoColor="dark" />
      <main className={s.root}>
        <nav className={s.breadcrumb}><a href={getLocalizedPath("/", lang)}>ホーム</a><span>›</span>{content.title}</nav>
        <nav className={s.topicNav} aria-label="海況ガイド">{landingLinks.map((item) => <a key={item.key} className={item.key === pageKey ? s.activeTopic : ""} href={item.href}>{item.label}</a>)}</nav>
        <header className={`${s.hero} ${s[pageKey]}`}>
          <div className={s.heroShade} />
          <div className={s.heroContent}><span>MIYAKO OCEAN GUIDE</span><h1>{content.heading}</h1><p>{content.lead}</p><a href={oceanPath} onClick={() => trackEvent("ocean_search_landing_click", { page_key: pageKey, placement: "hero" })}>Ocean Guideで詳細を見る <b>→</b></a></div>
        </header>

        <section className={s.conditions} aria-label={`${content.conditionLabel || "今日の宮古島"}の予報`}>
          <header><div><span>LIVE CONDITIONS</span><h2>{content.conditionLabel || "今日の宮古島"}</h2></div><em>最新予報</em></header>
          <div><p><span>天気</span><strong>{today.weather}</strong></p><p><span>降水確率</span><strong>{today.rain}%</strong></p><p><span>最大風速</span><strong>{today.wind}m/s</strong></p><p><span>波</span><strong>{today.wave}m</strong></p></div>
          <small>最新の場所別判定はOcean Guideで確認してください。</small>
        </section>

        <div className={s.contentGrid}><article className={s.article}>
          <span className={s.sectionEyebrow}>TODAY'S GUIDE</span><h2>{content.title}</h2><p>{content.body}</p>
          <a className={s.guideLink} href={oceanPath}>マップで今日おすすめの場所を確認 <b>→</b></a><h2>よくある質問</h2>
          {content.faq.map(([question, answer], index) => <section key={question}><i>0{index + 1}</i><div><h3>{question}</h3><p>{answer}</p></div></section>)}
        </article><aside className={s.sideGuide}><span>海へ行く前に</span><h2>現地の状況を確認</h2><p>予報が良くても、海の様子は場所と時間で変わります。白波や強い流れがある場合は無理をしないでください。</p><a href={oceanPath}>Ocean Guideを開く</a></aside></div>
        <nav className={s.directoryNav} aria-label="スポット・飲食店の詳細ガイド">
          <a href="/ocean-guide/spots">ビーチ・観光スポットを探す <b>→</b></a>
          <a href="/ocean-guide/restaurants">飲食店・カフェを探す <b>→</b></a>
        </nav>
      </main>
      <Footer />
    </>
  );
};

export default OceanSearchLandingPage;
